import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import AppLink from '../links/AppLink';
import {
  ContentCard,
  NewContentCard,
  useWindowDimensions,
  Image,
  ContentCardCheckbox,
} from 'edcom-ui';
import './SearchResults.less';
import styles from './SearchResults.module.less';
import SearchNoResult from './SearchNoResult';

const resultsVariants = {
  exit: {
    transition: { staggerChildren: 0.02, staggerDirection: -1 },
  },
};
const cardVariants = {
  initial: {
    scale: 0.96,
    y: 30,
    opacity: 0,
  },
  enter: {
    scale: 1,
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: [0.48, 0.15, 0.25, 0.96] },
  },
  exit: {
    scale: 0.6,
    y: 100,
    opacity: 0,
    transition: { duration: 0.2, ease: [0.48, 0.15, 0.25, 0.96] },
  },
};
export default function SearchResults({
  results = [],
  selected,
  handleSelected,
  suggestions,
  handleClickSuggestion,
  currentFilter,
  title,
  isNewTaxonomy,
  contentRequestInProcess,
}) {
  const router = useRouter();
  const windowDimensions = useWindowDimensions();
  const contentCards = results.map(contentModel => {
    const key = `${contentModel.type}:${contentModel.id}`;
    const resultClasses = clsx(styles.result, contentModel.type, {
      'premium-only': contentModel.premium,
    });
    let cta = (
      <ContentCardCheckbox
        checked={selected.indexOf(contentModel) !== -1}
        onChange={checked => {
          let newSelected = selected.slice();
          if (checked) {
            if (selected.length >= 50) {
              alert('Cannot select over 50!');
              return;
            }
            newSelected.push(contentModel);
            handleSelected([...selected, contentModel]);
          } else {
            for (let i = 0; i < newSelected.length; i++) {
              if (newSelected[i].id === contentModel.id) {
                newSelected.splice(i, 1);
              }
            }
            handleSelected(newSelected);
          }
        }}
        onClick={e => {
          e.nativeEvent.stopImmediatePropagation();
        }}
      />
    );

    let cardClassName = selected.indexOf(contentModel) === -1 ? '' : 'selected';

    let categoryName = isNewTaxonomy
      ? contentModel.category
      : contentModel.subject;

    return (
      <motion.div
        key={key}
        whileTap={{ scale: 0.95 }}
        variants={cardVariants}
        className={resultClasses}
      >
        <AppLink
          href={contentModel.url}
          className={clsx(
            styles.link,
            contentRequestInProcess && styles.disabled
          )}
        >
          {windowDimensions.width && windowDimensions.width >= 750 && (
            <NewContentCard
              type={contentModel.type}
              title={contentModel.title}
              description={
                contentModel.description == null ? '' : contentModel.description
              }
              grade={contentModel.grade}
              category={categoryName}
              thumbnail={contentModel.thumbnail}
              isPremium={contentModel.premium}
              isEL={contentModel.el}
              isInteractive={contentModel.isInteractive}
              isNew={contentModel.isNew}
              // TODO: get isAdmin property from contentModel
              isAdminOnly={false}
              withinGrid={true}
              className={cardClassName}
            >
              {cta}
            </NewContentCard>
          )}
          {windowDimensions.width && windowDimensions.width < 750 && (
            <ContentCard
              type={contentModel.type}
              title={contentModel.title}
              description={
                contentModel.description == null ? '' : contentModel.description
              }
              grade={contentModel.grade}
              category={categoryName}
              isPremium={contentModel.premium}
              isEL={contentModel.el}
              isInteractive={contentModel.isInteractive}
              isNew={contentModel.isNew}
              // TODO: get isAdmin property from contentModel
              isAdminOnly={false}
              withinGrid={true}
              className={cardClassName}
            >
              {contentModel.thumbnail && contentModel.thumbnail.src && (
                <Image src={contentModel.thumbnail.src} />
              )}
              {cta}
            </ContentCard>
          )}
        </AppLink>
      </motion.div>
    );
  });
  if (!results.length) {
    return (
      <SearchNoResult
        title={title}
        suggestions={suggestions}
        handleClickSuggestion={handleClickSuggestion}
        currentFilter={currentFilter}
      />
    );
  }

  return (
    <AnimatePresence exitBeforeEnter initial={false}>
      <motion.div
        key={router.asPath}
        className={clsx(styles.list, isNewTaxonomy && styles.isNewTaxonomy)}
        initial="initial"
        animate="enter"
        exit="exit"
        variants={resultsVariants}
      >
        {contentCards}
      </motion.div>
    </AnimatePresence>
  );
}

const contentCardPropTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  thumbnail: PropTypes.object,
  thumbnailWidth: PropTypes.number,
  subject: PropTypes.string,
  category: PropTypes.string,
  grade: PropTypes.string.isRequired,
  rating: PropTypes.number,
  new: PropTypes.bool,
  answersheet: PropTypes.bool.isRequired,
  premium: PropTypes.bool,
  el: PropTypes.bool.isRequired,
};

SearchResults.propTypes = {
  results: PropTypes.arrayOf(PropTypes.shape(contentCardPropTypes)),
  selected: PropTypes.array,
  handleSelected: PropTypes.func,
  suggestions: PropTypes.array,
  handleClickSuggestion: PropTypes.func,
  currentFilter: PropTypes.object,
  title: PropTypes.string,
  isNewTaxonomy: PropTypes.bool,
  contentRequestInProcess: PropTypes.bool,
};
